<script>
import Bike from "@/assets/svg/bike.svg";
import BikeCargo from "@/assets/svg/bike_cargo.svg";
import BikeCargoElectric from "@/assets/svg/bike_cargo_electric.svg";
import BikeElectric from "@/assets/svg/bike_electric.svg";
import CarLargeElectric from "@/assets/svg/car_large_electric.svg";
import CarSmallElectric from "@/assets/svg/car_small_electric.svg";
import CarLarge from "@/assets/svg/car_large.svg";
import CarSmall from "@/assets/svg/car_small.svg";
import CarTrailer from "@/assets/svg/car_trailer.svg";
import Pin from "@/assets/svg/pin.svg";
import Trailer from "@/assets/svg/trailer.svg";
import { loader } from "@/helpers/googleMaps";
import Vue from "vue";

export default Vue.extend({
  name: "MapMarker",
  components: {
    bike_regular: Bike,
    bike_electric: BikeElectric,
    bike_cargo_regular: BikeCargo,
    bike_cargo_electric: BikeCargoElectric,
    car_small: CarSmall,
    car_large: CarLarge,
    car_small_electric: CarSmallElectric,
    car_large_electric: CarLargeElectric,
    trailer: Trailer,
    car_trailer: CarTrailer,
    // Backups in case the pricing loanable type is not present
    bike: Bike,
    car: CarLarge,
    pin: Pin,
  },
  props: {
    loanable: {
      type: Object,
      default: () => null,
    },
    position: {
      type: Object,
      default: () => null,
    },
    map: {
      type: Object,
      required: true,
    },
    onclick: {
      type: Function,
      default: undefined,
    },
    ondrag: {
      type: Function,
      default: null,
    },
    outline: {
      type: Boolean,
      default: false,
    },
    ghost: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      marker: null,
    };
  },
  computed: {
    icon() {
      return this.loanable?.pricing_loanable_type;
    },
    glyph() {
      if (this.loanable?.pricing_loanable_type) {
        return this.loanable?.pricing_loanable_type;
      }

      return this.loanable?.type;
    },
  },
  watch: {
    position() {
      this.marker.position = this.position;
    },
    "loanable.position_google": {
      handler() {
        if (this.position) {
          return;
        }

        this.marker.position = this.loanable.position_google;
      },
    },
  },
  async mounted() {
    const { AdvancedMarkerElement } = await loader.importLibrary("marker");

    if (this.loanable) {
      this.marker = new AdvancedMarkerElement({
        map: this.map,
        position: this.position || this.loanable.position_google,
        content: this.$refs.marker,
        gmpClickable: !!this.onclick,
        gmpDraggable: !!this.ondrag,
        zIndex: this.loanable.available ? 12 : 8,
      });
    } else {
      this.marker = new AdvancedMarkerElement({
        map: this.map,
        position: this.position,
        gmpClickable: !!this.onclick,
        gmpDraggable: !!this.ondrag,
      });
    }

    if (this.onclick) {
      this.marker.addListener("click", () => this.onclick(this.loanable, this.marker));
    }
    if (this.ondrag) {
      this.marker.addListener("dragend", this.ondrag);
    }
  },
  destroyed() {
    if (this.marker) {
      this.marker.setMap(null);
      this.marker = null;
    }
  },
});
</script>

<template>
  <div class="d-none">
    <div
      v-if="loanable"
      ref="marker"
      class="loanable-map-marker"
      :class="{
        ghost: ghost,
        outline: outline,
        car: loanable.type === 'car',
        trailer: loanable.type === 'trailer',
        car_trailer: loanable.type === 'car_trailer',
        bike: loanable.type === 'bike',
      }"
    >
      <pin :id="`pin-${loanable.id}`" ref="pin" class="pin" />
      <component :is="glyph" ref="glyph" class="glyph" />
    </div>
  </div>
</template>

<style scoped lang="scss">
.loanable-map-marker {
  display: grid;
  grid-template-columns: auto;
  grid-template-rows: 5.5px auto;
  gap: 0;
  justify-items: center;
  pointer-events: none;
  user-select: none;
  width: 28px;
  height: 40px;

  &.ghost {
    opacity: 0.5;
  }

  .pin {
    display: block;
    overflow: visible;
    grid-area: 1;
  }

  .glyph {
    grid-area: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 16px;

    path {
      stroke-width: 0.2;
      fill: #fff;
      stroke: #fff;
    }
  }

  &.car {
    .pin path {
      fill: #4fb999;
      stroke: #4fb999;
    }

    &.outline {
      .pin path {
        stroke: #57917f;
      }
      .glyph path {
        stroke: #57917f;
        fill: #57917f;
      }
    }
  }

  &.trailer {
    .pin path {
      fill: #f9ca51;
      stroke: #f9ca51;
    }

    &.outline {
      .pin path {
        stroke: #c49e3c;
      }
      .glyph path {
        stroke: #c49e3c;
        fill: #c49e3c;
      }
    }
  }

  &.bike {
    .pin path {
      fill: #246aea;
      stroke: #246aea;
    }

    &.outline {
      .glyph path {
        stroke: #246aea;
        fill: #246aea;
      }
    }
  }

  &.car_trailer {
    .pin path {
      fill: #f95151;
      stroke: #f95151;
    }

    &.outline {
      .pin path {
        stroke: #c4473c;
      }
      .glyph path {
        stroke: #c4473c;
        fill: #c4473c;
      }
    }
  }

  &.outline .pin path {
    fill: #fff;
  }
}
</style>
